import { useCallback, useMemo, useState } from 'react';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

function normalise(value) {
  if (value == null || value === '' || value === '—') return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const str = String(value).trim();
  if (ISO_DATE.test(str)) return Date.parse(str) || str.toLowerCase();
  const num = Number(str.replace(/,/g, ''));
  if (str !== '' && !Number.isNaN(num)) return num;
  return str.toLowerCase();
}

function compare(a, b) {
  const va = normalise(a);
  const vb = normalise(b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  return String(va).localeCompare(String(vb), undefined, { numeric: true });
}

/**
 * Column sorting for tables.
 *
 * @param rows       array to sort
 * @param accessors  map of columnKey -> (row) => sortable primitive.
 *                   Falls back to row[columnKey].
 * @param initial    optional { key, dir } starting sort
 */
export function useTableSort(rows, accessors = {}, initial = null) {
  const [sort, setSort] = useState(initial);

  const toggleSort = useCallback((key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sort?.key) return rows;
    const accessor = accessors[sort.key] || ((row) => row?.[sort.key]);
    const factor = sort.dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => compare(accessor(a), accessor(b)) * factor);
    // accessors is recreated per render in most callers; key/dir drive the sort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);

  return { sorted, sort, toggleSort };
}
